/*
 * <review-summary> — collapsed star bar that expands to a score + per-star
 * histogram. Rating / count / histogram are sourced from Judge.me:
 *   1. Live Judge.me API (needs the shop's PUBLIC token via data-token) for the
 *      real histogram + freshest rating / count.
 *   2. The Judge.me preview badge on the page (.jdgm-prev-badge) — tokenless.
 *   3. The server-rendered values already in the markup (fallback).
 *
 * The component degrades gracefully: with no token (or a failed fetch) it still
 * shows the bar, score and "see all reviews" link; the histogram simply hides.
 */
if (!customElements.get('review-summary')) {
  class ReviewSummary extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-rs-toggle]');
      this.panel = this.querySelector('[data-rs-panel]');
      this.hist = this.querySelector('[data-rs-hist]');
      this.scaleMax = parseFloat(this.dataset.scaleMax) || 5;
      this.mergeAll = this.dataset.mergeAll === 'true';

      // Open on hover (desktop) and keyboard focus; click still toggles so it
      // remains usable on touch devices that have no hover.
      this.addEventListener('mouseenter', () => this.open());
      this.addEventListener('mouseleave', () => this.close());
      this.addEventListener('focusin', () => this.open());
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this.toggle());
      }

      // "See all reviews" should scroll down to the on-page Judge.me reviews
      // widget/carousel rather than relying on a hardcoded anchor that may not
      // exist on every template.
      this.allLink = this.querySelector('[data-rs-all]');
      if (this.allLink) {
        this.allLink.addEventListener('click', (e) => this.seeAllReviews(e));
      }

      if (this.mergeAll) {
        // Shop-wide: combine reviews across every product. The on-page badge is
        // product-specific, so don't seed from it — wait for the aggregate API.
        this.fetchAggregate();
      } else {
        // Pull tokenless rating/count straight from Judge.me's on-page badge.
        this.readPageBadge();
        // Then try the live API for the freshest numbers + the histogram.
        if (this.dataset.showHistogram === 'true' || this.dataset.token) {
          this.fetchLive();
        }
      }
    }

    toggle() {
      if (!this.panel) return;
      this.panel.hidden ? this.open() : this.close();
    }

    open() {
      if (!this.panel) return;
      this.panel.hidden = false;
      this.classList.add('rev-sum--open');
      this.toggleBtn && this.toggleBtn.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.classList.remove('rev-sum--open');
      this.toggleBtn && this.toggleBtn.setAttribute('aria-expanded', 'false');
    }

    /* ---------------- "See all reviews" scroll ---------------- */
    // The footer Customer Reviews cards carousel is the destination.
    findFooterReviews() {
      const node = document.getElementById('footer-customer-reviews');
      if (node) return node.closest('.shopify-section') || node;
      return document.querySelector('[id$="__17900987490208571e"]');
    }

    // Locate the on-page Judge.me reviews widget (the carousel / full list).
    findReviewsTarget() {
      const footerReviews = this.findFooterReviews();
      if (footerReviews) return footerReviews;

      // The review carousel at the bottom of the product page is
      // .jdgm-carousel-wrapper — prefer it above everything else.
      const carousel = document.querySelector('.jdgm-carousel-wrapper, .jdgm-carousel');
      if (carousel) return carousel;

      // 2) Honour an explicit anchor in the link if that element exists.
      const href = this.allLink && this.allLink.getAttribute('href');
      if (href && href.charAt(0) === '#' && href.length > 1) {
        const el = document.getElementById(href.slice(1));
        if (el) return el;
      }

      // 3) Fall back to other known Judge.me widget containers.
      const selectors = [
        '#judgeme_product_reviews',
        '.jdgm-rev-widg',
        '.jdgm-review-widget',
        '.jdgm-all-reviews-widget',
        '.jdgm-widget',
        '[data-jdgm-widget]',
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) return el;
      }
      return null;
    }

    seeAllReviews(e) {
      const target = this.findReviewsTarget();
      if (!target) return; // nothing found — let the default href behaviour run
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------------- Rendering helpers ---------------- */
    setRating(rating) {
      if (!(rating > 0)) return;
      const rounded = Math.round(rating * 10) / 10;
      this.querySelectorAll('[data-rs-rating]').forEach((el) => {
        el.textContent = rounded.toFixed(1);
      });
      const pct = Math.max(0, Math.min(100, (rating / this.scaleMax) * 100));
      this.querySelectorAll('[data-rs-stars-fg]').forEach((el) => {
        el.style.width = pct + '%';
      });
    }

    setCount(count) {
      if (!(count >= 0) || Number.isNaN(count)) return;
      const formatted = count.toLocaleString();
      this.querySelectorAll('[data-rs-count], [data-rs-total]').forEach((el) => {
        el.textContent = formatted;
      });
    }

    // Accepts a map { 5: {freq, pct}, 4: {...}, ... }. Renders each bar's
    // width + percentage label, preferring Judge.me's own percentage and
    // falling back to freq / total when only counts are available.
    renderHistogram(byStar) {
      if (!this.hist || !byStar) return;
      const stars = [5, 4, 3, 2, 1];
      const totalFreq = stars.reduce(
        (sum, s) => sum + ((byStar[s] && byStar[s].freq) || 0),
        0
      );
      let rendered = false;

      stars.forEach((star) => {
        const row = this.hist.querySelector('[data-rs-row="' + star + '"]');
        if (!row) return;
        const entry = byStar[star] || {};
        let pct = entry.pct;
        if ((pct == null || Number.isNaN(pct)) && totalFreq > 0) {
          pct = ((entry.freq || 0) / totalFreq) * 100;
        }
        if (pct == null || Number.isNaN(pct)) pct = 0;

        const fill = row.querySelector('[data-rs-fill]');
        const label = row.querySelector('[data-rs-pct]');
        if (fill) fill.style.width = pct + '%';
        if (label) label.textContent = Math.round(pct) + '%';
        rendered = true;
      });

      if (rendered) this.hist.hidden = false;
    }

    /* ---------------- Data sources ---------------- */
    readPageBadge() {
      const badge = document.querySelector(
        '.jdgm-prev-badge[data-average-rating], .jdgm-prev-badge[data-number-of-reviews]'
      );
      if (!badge) return;
      const rating = parseFloat(badge.getAttribute('data-average-rating'));
      const count = parseInt(badge.getAttribute('data-number-of-reviews'), 10);
      if (rating > 0) this.setRating(rating);
      if (count >= 0) this.setCount(count);
    }

    // Judge.me widget endpoints usually wrap their payload as {"widget": ...}.
    // Return the inner value (HTML or number string) or the raw text.
    unwrap(text) {
      const str = String(text == null ? '' : text).trim();
      try {
        const json = JSON.parse(str);
        if (json && typeof json === 'object' && typeof json.widget === 'string') {
          return json.widget;
        }
        if (typeof json === 'string') return json;
      } catch (e) {
        /* not JSON — use the raw text */
      }
      return str;
    }

    // Parse a "single number" response (JSON object, bare number, or text).
    parseNumber(text) {
      if (text == null) return NaN;
      const str = String(text).trim();
      try {
        const json = JSON.parse(str);
        if (typeof json === 'number') return json;
        if (json && typeof json === 'object') {
          const keys = [
            'average_rating', 'rating', 'average', 'value',
            'count', 'number_of_reviews', 'reviews_count', 'total',
          ];
          for (const k of keys) {
            if (typeof json[k] === 'number') return json[k];
          }
          for (const k in json) {
            if (typeof json[k] === 'number') return json[k];
          }
        }
      } catch (e) {
        /* not JSON — fall through to text parsing */
      }
      const m = str.replace(/,/g, '').match(/[\d.]+/);
      return m ? parseFloat(m[0]) : NaN;
    }

    // Combine rating / count / histogram across every product + the store,
    // using Judge.me's public shop-wide widget endpoints.
    async fetchAggregate() {
      const token = (this.dataset.token || '').trim();
      if (!token) return;

      const shops = [this.dataset.shop, this.dataset.shopAlt].filter(Boolean);
      const base = 'https://api.judge.me/api/v1/widgets/';

      for (const shop of shops) {
        const q =
          '?shop_domain=' + encodeURIComponent(shop) +
          '&api_token=' + encodeURIComponent(token);
        try {
          const wantHist = this.hist && this.dataset.showHistogram === 'true';
          const [ratingRes, countRes, pageRes] = await Promise.all([
            fetch(base + 'all_reviews_rating' + q, { headers: { Accept: 'application/json' } }),
            fetch(base + 'all_reviews_count' + q, { headers: { Accept: 'application/json' } }),
            wantHist
              ? fetch(base + 'all_reviews_page' + q, { headers: { Accept: 'application/json' } })
              : Promise.resolve(null),
          ]);

          let ok = false;

          if (ratingRes && ratingRes.ok) {
            const rating = this.parseNumber(this.unwrap(await ratingRes.text()));
            if (rating > 0) { this.setRating(rating); ok = true; }
          }

          if (countRes && countRes.ok) {
            const count = this.parseNumber(this.unwrap(await countRes.text()));
            if (count >= 0 && !Number.isNaN(count)) {
              this.setCount(count);
              ok = true;
            }
          }

          if (pageRes && pageRes.ok) {
            const raw = await pageRes.text();
            // The all_reviews_page payload carries the histogram in its
            // "all_reviews_header" field (not "widget").
            let headerHtml = raw;
            try {
              const json = JSON.parse(raw);
              headerHtml =
                json.all_reviews_header || json.widget || json.all_reviews || raw;
            } catch (e) {
              /* not JSON — treat the raw text as HTML */
            }
            const byStar = this.parseHistogram(headerHtml);
            if (byStar) this.renderHistogram(byStar);
          }

          if (ok) return;
        } catch (e) {
          /* try the next shop domain, else keep server/fallback values */
        }
      }
    }

    async fetchLive() {
      const token = (this.dataset.token || '').trim();
      const id = this.dataset.productId;
      if (!token || !id) return;

      const shops = [this.dataset.shop, this.dataset.shopAlt].filter(Boolean);
      for (const shop of shops) {
        try {
          const url =
            'https://api.judge.me/api/v1/widgets/product_review' +
            '?shop_domain=' + encodeURIComponent(shop) +
            '&api_token=' + encodeURIComponent(token) +
            '&external_id=' + encodeURIComponent(id) +
            '&per_page=1';
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) continue;
          const data = await res.json();
          if (data && data.widget && this.applyWidget(data.widget)) return;
        } catch (e) {
          /* try the next shop domain, else keep server/badge values */
        }
      }
    }

    applyWidget(html) {
      let doc;
      try {
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (e) {
        return false;
      }

      const widg =
        doc.querySelector('[data-average-rating]') ||
        doc.querySelector('.jdgm-rev-widg');
      if (widg) {
        const rating = parseFloat(widg.getAttribute('data-average-rating'));
        const count = parseInt(widg.getAttribute('data-number-of-reviews'), 10);
        if (rating > 0) this.setRating(rating);
        if (count >= 0) this.setCount(count);
      }

      const freqByStar = this.parseHistogram(doc);
      if (freqByStar) this.renderHistogram(freqByStar);
      return true;
    }

    // Histogram: each row carries its star rating + a frequency. Parse
    // defensively across known Judge.me markup variants. Accepts an HTML
    // string or an already-parsed document. Returns {5:.., 4:..} or null.
    parseHistogram(htmlOrDoc) {
      let doc = htmlOrDoc;
      if (typeof htmlOrDoc === 'string') {
        try {
          doc = new DOMParser().parseFromString(htmlOrDoc, 'text/html');
        } catch (e) {
          return null;
        }
      }
      if (!doc || !doc.querySelectorAll) return null;

      const byStar = {};
      let found = false;
      const rows = doc.querySelectorAll(
        '.jdgm-histogram__row, .jdgm-histogram tr, [class*="histogram__row"]'
      );
      rows.forEach((row, index) => {
        let star = parseInt(row.getAttribute('data-rating'), 10);
        if (!star) {
          const labelled = row.querySelector('[data-rating]');
          if (labelled) star = parseInt(labelled.getAttribute('data-rating'), 10);
        }
        if (!star) {
          const m = (row.textContent || '').match(/([1-5])\s*star/i);
          if (m) star = parseInt(m[1], 10);
        }
        if (!star) star = 5 - index; // rows are ordered 5 -> 1

        // Frequency: from the row attribute, a child node, or "(123)" text.
        let freq = parseInt(row.getAttribute('data-frequency'), 10);
        if (Number.isNaN(freq)) {
          const freqEl = row.querySelector('[data-frequency], .jdgm-histogram__frequency');
          if (freqEl) {
            freq = parseInt(freqEl.getAttribute('data-frequency'), 10);
            if (Number.isNaN(freq)) freq = parseInt((freqEl.textContent || '').replace(/[^0-9]/g, ''), 10);
          }
        }

        // Percentage: from the row attribute, a child node, or "93%" text.
        let pct = parseFloat(row.getAttribute('data-percentage'));
        if (Number.isNaN(pct)) {
          const pctEl = row.querySelector('[data-percentage], .jdgm-histogram__percentage');
          if (pctEl) {
            pct = parseFloat(pctEl.getAttribute('data-percentage'));
            if (Number.isNaN(pct)) pct = parseFloat((pctEl.textContent || '').replace(/[^0-9.]/g, ''));
          }
        }

        if (Number.isNaN(freq) && Number.isNaN(pct)) return;
        if (star >= 1 && star <= 5) {
          byStar[star] = {
            freq: Number.isNaN(freq) ? null : freq,
            pct: Number.isNaN(pct) ? null : pct,
          };
          found = true;
        }
      });

      return found ? byStar : null;
    }
  }

  customElements.define('review-summary', ReviewSummary);
}
